import { Modal } from '@lume/ui-components';

import type { ProtectionValidation } from '../types/protection.types';

/**
 * ProtectionBlockModal — Sprint Backend Protection v1 Agent C (Item 226) +
 * flag F1.3 (bloqueio honesto — decisão de produto do TL).
 *
 * Modal INFORMATIVO para severity ∈ {high, critical} + action='block'. O
 * bloqueio é HARD e incondicional no Engine (só ocorre em conteúdo `critical`
 * ou `high` em tenant Regulated) — NÃO há override do atendente. O modal
 * apenas dá contexto para o atendente EDITAR a mensagem e reenviar:
 *   - Motivo (`reason`) do bloqueio.
 *   - Categorias detectadas + regex hits (labels privacy-preserving).
 *   - Sugestão de redação alternativa (`redactionSuggestion`), se presente.
 *   - Único botão de ação: "Editar mensagem" (fecha → foco volta ao composer,
 *     rascunho preservado).
 *
 * F1.3: o botão "Enviar mesmo assim" foi REMOVIDO. O Engine nunca leu o header
 * `X-Protection-Acknowledged`, então o override era desonesto (silêncio
 * enganoso — o reenvio caía no mesmo bloqueio). Sem `onConfirm`, o DS Modal
 * renderiza só o botão de fechar/cancelar.
 *
 * Acessibilidade canonical:
 *   - Modal DS v2.2.0 já provê role="dialog" + aria-modal + focus trap.
 *   - persistent=false canonical → atendente fecha via X, Esc, click overlay
 *     ou "Editar mensagem".
 *
 * Defense-in-depth canonical: caller (Composer) só renderiza modal quando
 * `validation.action === 'block'` (capability gate aplicado no hook).
 */
export function ProtectionBlockModal({
  open,
  validation,
  onCancel,
}: {
  open: boolean;
  validation: ProtectionValidation | null;
  onCancel: () => void;
}): JSX.Element | null {
  if (!open || validation === null) return null;

  const isCritical = validation.severity === 'critical';
  const titleText = isCritical
    ? 'Envio bloqueado — risco crítico detectado'
    : 'Envio bloqueado — revise antes de prosseguir';

  return (
    <Modal
      isOpen={open}
      onClose={onCancel}
      title={titleText}
      cancelLabel="Editar mensagem"
      maxWidth="540px"
    >
      <div
        data-testid="protection-block-modal-body"
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--space-3, 12px)',
          fontSize: 'var(--text-sm, 13px)',
          color: 'var(--fg-1, #111)',
        }}
      >
        {/* Razão canonical do bloqueio. */}
        <div>
          <p
            style={{
              fontSize: 'var(--text-2xs, 11px)',
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
              fontWeight: 600,
              color: 'var(--fg-2, #374151)',
              marginBottom: 4,
            }}
          >
            Motivo
          </p>
          <p
            data-testid="protection-block-modal-reason"
            style={{
              padding: 'var(--space-2, 8px) var(--space-3, 12px)',
              background: 'var(--danger-bg, #fef2f2)',
              color: 'var(--danger-fg, #991b1b)',
              borderLeft: '3px solid var(--danger-fg, #dc2626)',
              borderRadius: 'var(--radius-sm, 5px)',
              margin: 0,
            }}
          >
            {validation.reason}
          </p>
        </div>

        {/* Categorias canonical detectadas. */}
        {validation.categories.length > 0 && (
          <div>
            <p
              style={{
                fontSize: 'var(--text-2xs, 11px)',
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
                fontWeight: 600,
                color: 'var(--fg-2, #374151)',
                marginBottom: 4,
              }}
            >
              Categorias detectadas
            </p>
            <ul
              data-testid="protection-block-modal-categories"
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 6,
                listStyle: 'none',
                padding: 0,
                margin: 0,
              }}
            >
              {validation.categories.map((cat) => (
                <li
                  key={cat}
                  style={{
                    padding: '2px 8px',
                    background: 'var(--danger-bg, #fef2f2)',
                    color: 'var(--danger-fg, #991b1b)',
                    borderRadius: 'var(--radius-sm, 5px)',
                    fontSize: 'var(--text-xs, 12px)',
                    fontFamily: 'var(--font-mono, monospace)',
                  }}
                >
                  {cat}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Regex hits canonical (camada 0 — apenas categorias por privacidade). */}
        {validation.regexHits !== undefined && validation.regexHits.length > 0 && (
          <div>
            <p
              style={{
                fontSize: 'var(--text-2xs, 11px)',
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
                fontWeight: 600,
                color: 'var(--fg-2, #374151)',
                marginBottom: 4,
              }}
            >
              Padrões detectados (camada 0)
            </p>
            <ul
              data-testid="protection-block-modal-regex-hits"
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 6,
                margin: 0,
                padding: 0,
                listStyle: 'none',
              }}
            >
              {validation.regexHits.map((hit, idx) => (
                <li
                  key={`${hit}-${idx.toString()}`}
                  style={{
                    fontSize: 'var(--text-xs, 12px)',
                    fontFamily: 'var(--font-mono, monospace)',
                    color: 'var(--danger-fg, #991b1b)',
                    backgroundColor: 'var(--danger-bg-subtle, #fef2f2)',
                    border: '1px solid var(--danger-border, #fecaca)',
                    borderRadius: 4,
                    padding: '2px 8px',
                  }}
                >
                  {hit}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Sugestão de redação canonical. */}
        {validation.redactionSuggestion != null && validation.redactionSuggestion.length > 0 && (
          <div>
            <p
              style={{
                fontSize: 'var(--text-2xs, 11px)',
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
                fontWeight: 600,
                color: 'var(--fg-2, #374151)',
                marginBottom: 4,
              }}
            >
              Sugestão de redação alternativa
            </p>
            <pre
              data-testid="protection-block-modal-suggestion"
              style={{
                padding: 'var(--space-2, 8px) var(--space-3, 12px)',
                background: 'var(--bg-subtle, #f9fafb)',
                border: '1px solid var(--border-default, #e5e7eb)',
                borderRadius: 'var(--radius-sm, 5px)',
                fontFamily: 'var(--font-mono, monospace)',
                fontSize: 'var(--text-xs, 12px)',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                margin: 0,
              }}
            >
              {validation.redactionSuggestion}
            </pre>
          </div>
        )}

        {/* Aviso canonical. */}
        <p
          data-testid="protection-block-modal-warning"
          style={{
            fontSize: 'var(--text-xs, 12px)',
            color: 'var(--fg-2, #374151)',
            fontStyle: 'italic',
            margin: 0,
            padding: 'var(--space-2, 8px)',
            borderRadius: 'var(--radius-sm, 5px)',
            background: 'var(--bg-subtle, #f9fafb)',
          }}
        >
          {isCritical ? (
            <>
              <strong>Crítico:</strong> risco crítico detectado. Este conteúdo não pode ser enviado
              pela política de proteção do tenant. Edite a mensagem e reenvie.
            </>
          ) : (
            <>
              Este conteúdo não pode ser enviado pela política de proteção do tenant. Edite a
              mensagem e reenvie.
            </>
          )}
        </p>

        {validation.timeoutFallback === true && (
          <p
            data-testid="protection-block-modal-timeout-fallback"
            style={{
              fontSize: 'var(--text-2xs, 11px)',
              color: 'var(--fg-3, #6b7280)',
              fontStyle: 'italic',
              margin: 0,
            }}
          >
            IA timeoutou — bloqueio baseado em fallback regex local.
          </p>
        )}
      </div>
    </Modal>
  );
}
